import React, { useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useOnClickOutside } from '@vodafoneis/sjonvarpskjarni-js-lib';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { ButtonBaseContainer, ButtonTitle } from './Button';
import { BUTTON_BACKGROUND, BUTTON_BACKGROUND_ACTIVE, SECONDARY_DARKENED } from '../styles/colors';

type DropdownItemInterface = {
	key: string | number;
	title: string;
};

type DropdownItemOnChange = (value: string | number) => void;

type DropdownProps = {
	value: string | number;
	items: DropdownItemInterface[];
	onChange: DropdownItemOnChange;
};

const DropdownContainer = styled.div`
	position: relative;
	display: inline-block;
`;

const DropdownButton = styled(ButtonBaseContainer)`
	display: flex;
	height: auto;
	justify-content: space-between;
`;

const DropdownButtonIcon = styled(FontAwesomeIcon)`
	margin-left: 20px;
	color: ${SECONDARY_DARKENED};
`;

const DropdownItems = styled.ul<{ open?: boolean }>`
	display: ${(props) => (props.open ? 'block' : 'none')};
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	background: ${BUTTON_BACKGROUND};
	z-index: 99;
	list-style: none;
	margin: 0;
	padding: 0;
	overflow: hidden;
	border-radius: 6px;
`;

const DropdownItemContainer = styled.li<{ active?: boolean }>`
	text-align: center;
	padding: 10px 15px;
	line-height: 1.3;
	color: ${SECONDARY_DARKENED};
	font-size: 22px;
	cursor: pointer;
	font-weight: ${(props) => (props.active ? 'bold' : 'normal')};

	@media (hover) {
		&:hover {
			background: ${BUTTON_BACKGROUND_ACTIVE};
		}
	}
`;

export const DropdownItem: React.FC<{ item: DropdownItemInterface; onChange: DropdownItemOnChange; active?: boolean }> = ({ item, onChange, active }) => {
	const { key, title } = item;

	const onClick = useCallback(
		(e) => {
			e.preventDefault();
			onChange(key);
		},
		[key, onChange]
	);

	return (
		<DropdownItemContainer onClick={onClick} active={active}>
			{title}
		</DropdownItemContainer>
	);
};

export const Dropdown: React.FC<DropdownProps> = ({ value, items, onChange }) => {
	const ref = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);

	useOnClickOutside(ref, () => setOpen(false));

	const onButtonClick = useCallback(
		(e) => {
			e.preventDefault();
			setOpen(!open);
		},
		[open]
	);

	const onChangeHandler = useCallback(
		(v) => {
			setOpen(false);
			onChange(v);
		},
		[onChange]
	);

	const title = useMemo(() => {
		if (value === undefined || !items) return null;

		return items.find((item) => item.key === value)?.title;
	}, [items, value]);

	if (value === undefined || !items) return null;

	return (
		<DropdownContainer ref={ref}>
			<DropdownButton withSubtitle={false} onClick={onButtonClick}>
				<ButtonTitle withSubtitle={false}>{title}</ButtonTitle>
				<DropdownButtonIcon icon={faCaretDown} />
			</DropdownButton>
			<DropdownItems open={open}>
				{items.map((item) => (
					<DropdownItem key={item.key} item={item} onChange={onChangeHandler} active={item.key === value} />
				))}
			</DropdownItems>
		</DropdownContainer>
	);
};
